import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import { LINE_OFFICIAL_URL } from '@/pages/gmb-audit/consult/config';

/**
 * 隱私權與個資蒐集說明（V1 Conversion-1.2）。
 * 僅作為 Consult Form 必要的公開說明頁，非會員隱私中心 / Cookie Center / Consent Manager。
 * 純靜態公開內容，不新增任何資料蒐集、追蹤或 Backend 依賴。
 */
export default function GmbAuditPrivacyPage() {
  return (
    <SiteLayout>
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="ds-container py-12 md:py-16">
          <p className="text-sm font-semibold uppercase tracking-wider text-primary-700">隱私權說明</p>
          <h1 className="mt-3 text-[2rem] font-bold leading-tight text-foreground-950 md:text-[2.75rem]">
            隱私權與個資蒐集說明
          </h1>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-foreground-600 md:text-[1.1875rem]">
            以下說明天地人網在「Google 商家快速健診」與諮詢服務中，如何處理您主動提供的聯絡資料。
          </p>
        </div>
      </section>

      <section className="ds-container py-12 md:py-16">
        <div className="mx-auto flex max-w-3xl flex-col gap-6">
          {/* 一、資料蒐集目的 */}
          <article className="rounded-2xl border border-background-200 bg-background-50 p-5 shadow-card md:p-7">
            <h2 className="flex items-center gap-2.5 text-lg font-bold text-foreground-950 md:text-xl">
              <i className="ri-flag-2-line text-primary-700" />
              一、資料蒐集目的
            </h2>
            <p className="mt-3 text-base leading-relaxed text-foreground-700 md:text-[1.1875rem]">
              當您主動提交諮詢需求時，天地人網會使用您提供的資料用於：
            </p>
            <ul className="mt-3 flex flex-col gap-2">
              {['回覆您的諮詢', '確認聯絡需求', '安排後續溝通', '提供與您主動詢問之服務相關的聯繫'].map(
                (item) => (
                  <li key={item} className="flex items-start gap-2 text-base leading-relaxed text-foreground-700">
                    <i className="ri-checkbox-circle-line mt-0.5 text-lg text-primary-700" />
                    <span>{item}</span>
                  </li>
                ),
              )}
            </ul>
            <p className="mt-3 text-base leading-relaxed text-foreground-600">
              您主動提供的資料不會用於任意行銷、出售給第三方，或作為第三方廣告追蹤用途。
            </p>
          </article>

          {/* 二、可能蒐集的資料 */}
          <article className="rounded-2xl border border-background-200 bg-background-50 p-5 shadow-card md:p-7">
            <h2 className="flex items-center gap-2.5 text-lg font-bold text-foreground-950 md:text-xl">
              <i className="ri-file-list-3-line text-primary-700" />
              二、可能蒐集的資料
            </h2>
            <p className="mt-3 text-base leading-relaxed text-foreground-700 md:text-[1.1875rem]">
              依目前的諮詢表單設計，可能蒐集的資料為：
            </p>
            <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {[
                '姓名／稱呼',
                '店家／公司名稱',
                '聯絡電話',
                'Email（若填寫）',
                'LINE ID（若填寫）',
                '所在縣市（若填寫）',
                '您選擇的需求',
                '方便聯絡時段',
                '您主動填寫的其他內容',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-base leading-relaxed text-foreground-700">
                  <i className="ri-checkbox-blank-circle-line mt-0.5 text-base text-primary-600" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </article>

          {/* 三、資料使用方式 */}
          <article className="rounded-2xl border border-background-200 bg-background-50 p-5 shadow-card md:p-7">
            <h2 className="flex items-center gap-2.5 text-lg font-bold text-foreground-950 md:text-xl">
              <i className="ri-shield-user-line text-primary-700" />
              三、資料使用方式
            </h2>
            <p className="mt-3 text-base leading-relaxed text-foreground-700 md:text-[1.1875rem]">
              上述資料僅用於處理本次諮詢、聯絡需求及後續服務溝通。
            </p>
          </article>

          {/* 四、Google 商家健診資料 */}
          <article className="rounded-2xl border border-background-200 bg-background-50 p-5 shadow-card md:p-7">
            <h2 className="flex items-center gap-2.5 text-lg font-bold text-foreground-950 md:text-xl">
              <i className="ri-google-line text-primary-700" />
              四、Google 商家健診資料
            </h2>
            <p className="mt-3 text-base leading-relaxed text-foreground-700 md:text-[1.1875rem]">
              Google 商家快速健診使用 Google Maps / Places 可取得之公開資料進行分析。
            </p>
            <p className="mt-2 text-base leading-relaxed text-foreground-600">
              本工具不會因使用健診功能而取得您的 Google Business Profile 後台登入資料。
            </p>
          </article>

          {/* 五、歷史健診紀錄 */}
          <article className="rounded-2xl border border-background-200 bg-background-50 p-5 shadow-card md:p-7">
            <h2 className="flex items-center gap-2.5 text-lg font-bold text-foreground-950 md:text-xl">
              <i className="ri-history-line text-primary-700" />
              五、歷史健診紀錄
            </h2>
            <p className="mt-3 text-base leading-relaxed text-foreground-700 md:text-[1.1875rem]">
              目前版本未使用會員登入。部分歷史健診查找能力以目前瀏覽器環境為單位，不是 Google 帳號或會員身份紀錄。
            </p>
            <p className="mt-2 text-base leading-relaxed text-foreground-600">
              清除瀏覽器資料、使用不同瀏覽器或更換裝置後，可能無法自動找回。
            </p>
          </article>

          {/* 六、聯絡方式 */}
          <article className="rounded-2xl border border-primary-200 bg-primary-50/70 p-5 shadow-card md:p-7">
            <h2 className="flex items-center gap-2.5 text-lg font-bold text-foreground-950 md:text-xl">
              <i className="ri-customer-service-2-line text-primary-800" />
              六、聯絡方式
            </h2>
            <p className="mt-3 text-base leading-relaxed text-foreground-700 md:text-[1.1875rem]">
              若您對所提供的聯絡資料或本次諮詢有疑問，可透過 LINE 與天地人網聯繫。
            </p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href={LINE_OFFICIAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-14 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary-700 px-6 text-base font-semibold text-background-50 transition-colors hover:bg-primary-800 sm:w-auto"
              >
                <i className="ri-line-fill text-lg" />
                加入 LINE 詢問
              </a>
              <Link
                to="/gmb-audit/consult"
                className="inline-flex h-14 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-background-400 bg-background-50 px-6 text-base font-semibold text-foreground-700 transition-colors hover:bg-background-100 sm:w-auto"
              >
                <i className="ri-edit-2-line text-lg" />
                前往預約諮詢
              </Link>
            </div>
          </article>
        </div>
      </section>
    </SiteLayout>
  );
}